// DeckBuilderPage.jsx
import React, { useState, useEffect } from 'react';
import './DeckBuilderPage.css';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { playSound } from '../utils/soundPlayer';
import { getFirestore, collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { shuffleArray, tournamentShuffle } from '../logic/DuelLogic';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { loadDeckFromStorage } from '../logic/DuelLogic';



const DeckBuilderPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [mainDeck, setMainDeck] = useState([]);
  const [extraDeck, setExtraDeck] = useState([]);
  const [sideDeck, setSideDeck] = useState([]);
  const [deckName, setDeckName] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [deckNameInput, setDeckNameInput] = useState('');
  const [savedDecks, setSavedDecks] = useState([]);
  const [selectedDeckIndex, setSelectedDeckIndex] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [cardDatabase, setCardDatabase] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showCardInfoModal, setShowCardInfoModal] = useState(false);
  const { t } = useTranslation();
  const [showYDKEImport, setShowYDKEImport] = useState(false);
  const [ydkeInput, setYDKEInput] = useState('');
  const [onlineDeckCount, setOnlineDeckCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingOnline, setSavingOnline] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [savingAsNew, setSavingAsNew] = useState(false);
  
  // Replace expandable panels with modals
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const [showTournamentModal, setShowTournamentModal] = useState(false);
  const [userTournaments, setUserTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(false);

  // Close all modals when clicking outside
  const closeAllModals = () => {
    setShowActionsModal(false);
    setShowToolsModal(false);
    setShowImportExportModal(false);
    setShowTournamentModal(false);
  };


  const saveDeckOnline = async () => {
    setSavingOnline(true);
    try {
      const user = getAuth().currentUser;
      if (!user) {
        toast.error('You must be logged in to save online.');
        return;
      }
      const db = getFirestore();
      const decksRef = collection(db, 'users', user.uid, 'decks');
      
      const name = deckNameInput || 'Unnamed Deck';
      const main = mainDeck.map(c => Number(c.id));
      const extra = extraDeck.map(c => Number(c.id));
      const side = sideDeck.map(c => Number(c.id));
      
      // Check if a deck with the same name already exists
      const snapshot = await getDocs(decksRef);
      const existingDeck = snapshot.docs.find(doc => doc.data().name === name);
      
      if (existingDeck) {
        // Update existing deck
        const deckRef = doc(db, 'users', user.uid, 'decks', existingDeck.id);
        await updateDoc(deckRef, {
          main,
          extra,
          side,
          updatedAt: serverTimestamp()
        });
        toast.success('✅ Existing deck updated online!');
      } else {
        // Check if user has reached the limit of 5 decks
        if (snapshot.docs.length >= 5) {
          toast.error('You can only save 5 decks online.');
          return;
        }
        
        // Create new deck
        const newDeck = {
          name,
          main,
          extra,
          side,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        const newDocRef = doc(decksRef); // auto-generated ID
        await setDoc(newDocRef, newDeck);
        toast.success('✅ New deck saved online!');
      }
    } catch (e) {
      console.error('Save online error:', e);
      toast.error('Failed to save deck online.');
    } finally {
      setSavingOnline(false);
    }
  };


  const handleStartDuel = async () => {
    console.log('🎴 Hand Test Button Pressed:', {
      selectedDeckIndex,
      savedDecksLength: savedDecks.length,
      selectedDeck: savedDecks[selectedDeckIndex]
    });
    
    const selectedDeck = savedDecks[selectedDeckIndex];
    if (!selectedDeck) {
      toast.error('Please select a deck first!');
      return;
    }

    let resolvedDeck = null;

    if (selectedDeck.source === 'local') {
      resolvedDeck = selectedDeck;
    }
    else if (selectedDeck.source === 'online' && selectedDeck.id) {
      try {
        const user = getAuth().currentUser;
        if (!user) {
          toast.error('You must be logged in.');
          return;
        }

        const db = getFirestore();
        const deckRef = doc(db, 'users', user.uid, 'decks', selectedDeck.id);
        const snapshot = await getDoc(deckRef);

        if (!snapshot.exists()) {
          toast.error('Deck not found.');
          return;
        }

        const deckData = snapshot.data();

        // ✅ Resolve IDs to full card objects
        let cardDB = cardDatabase;
        if (!cardDB) {
          const res = await fetch('https://db.ygoprodeck.com/api/v7/cardinfo.php');
          const data = await res.json();
          cardDB = data.data || [];
          cardDB = Array.from(new Map(cardDB.map(c => [String(c.id), c])).values());
          setCardDatabase(cardDB);
        }

        const resolve = (idList) =>
          idList.map(id =>
            cardDB.find(c => String(c.id) === String(id)) ||
            cardDB.find(c => c.misc_info?.alias?.includes(Number(id))) ||
            cardDB.find(c => c.card_images?.some(img => String(img.id) === String(id))) ||
            {
              id,
              name: `Unknown (${id})`,
              card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
            }
          );

        resolvedDeck = {
          name: deckData.name || 'Online Deck',
          main: resolve(deckData.main || []),
          extra: resolve(deckData.extra || []),
          side: resolve(deckData.side || []),
        };
      } catch (err) {
        toast.error('Failed to load deck from server.');
        return;
      }
    }

    if (!resolvedDeck) {
      toast.error('Failed to load selected deck.');
      return;
    }

    // ✅ HAND TEST needs 5 cards in hand
    // Use tournamentShuffle for better randomization
    const shuffledMain = tournamentShuffle(resolvedDeck.main);
    
    // Ensure we have enough cards for hand test
    if (shuffledMain.length < 5) {
      toast.error('Deck must have at least 5 cards for hand test!');
      return;
    }

    const hand = shuffledMain.slice(0, 5);
    const restMain = shuffledMain.slice(5);

    console.log('🎴 Hand Test - Shuffled Deck:', {
      originalDeckSize: resolvedDeck.main.length,
      shuffledDeckSize: shuffledMain.length,
      handSize: hand.length,
      remainingDeckSize: restMain.length,
      handCards: hand.map(c => c.name)
    });

    // ✅ Save ONLY for hand test, in a separate key
    localStorage.setItem('handTestDeck', JSON.stringify({
      ...resolvedDeck,
      main: restMain,
      hand,
    }));

    navigate('/duel-drag');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      const localDecks = JSON.parse(localStorage.getItem('savedDecks') || '[]')
        .map(deck => ({ ...deck, source: 'local' }));

      let onlineDecks = [];

      if (user) {
        try {
          const db = getFirestore();
          const snapshot = await getDocs(collection(db, 'users', user.uid, 'decks'));

          onlineDecks = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'online'
          }));

          // Count all online decks for the limit
          setOnlineDeckCount(onlineDecks.length);
          
          // Fetch user tournaments
          await fetchUserTournaments(user.uid);
        } catch (err) {
          console.error('Error fetching online decks:', err);
        }
      }

      const merged = [...localDecks, ...onlineDecks];
      setSavedDecks(merged);
      setHasLoaded(true);
      
      // Store the last selected index for restoration after savedDecks is set
      const lastSelectedIndex = localStorage.getItem('lastSelectedDeckIndex');
      if (lastSelectedIndex !== null) {
        const index = parseInt(lastSelectedIndex);
        if (index >= 0 && index < merged.length) {
          // We'll restore this in a separate useEffect after savedDecks is set
          setSelectedDeckIndex(index);
        } else {
          // Remove invalid index from localStorage
          localStorage.removeItem('lastSelectedDeckIndex');
        }
      }
    });

    return () => unsubscribe();
  }, []);



  useEffect(() => {
    if (!hasLoaded) return;

    const onlyLocal = savedDecks.filter(deck => deck.source === 'local');

    // ✅ Don't overwrite if there are no real local decks
    if (onlyLocal.length === 0) return;

    localStorage.setItem('savedDecks', JSON.stringify(onlyLocal));
  }, [savedDecks, hasLoaded]);

  // Restore the last selected deck after savedDecks is loaded
  useEffect(() => {
    if (!hasLoaded || savedDecks.length === 0) return;
    
    const lastSelectedIndex = localStorage.getItem('lastSelectedDeckIndex');
    if (lastSelectedIndex !== null) {
      const index = parseInt(lastSelectedIndex);
      if (index >= 0 && index < savedDecks.length) {
        // Now we can safely load the deck since savedDecks is properly set
        loadDeck(index);
      } else {
        // Remove invalid index from localStorage
        localStorage.removeItem('lastSelectedDeckIndex');
        setSelectedDeckIndex(null);
      }
    }
  }, [savedDecks, hasLoaded]);





  const searchCards = async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      let allCards = cardDatabase;

      // Fetch once if not already cached
      if (!allCards) {
        const res = await fetch(`https://db.ygoprodeck.com/api/v7/cardinfo.php`);
        const data = await res.json();
        allCards = data.data || [];
        setCardDatabase(allCards);
      }

      const q = query.trim().toLowerCase();

      const filtered = allCards.filter(card => {
        const nameMatch = card.name?.toLowerCase().includes(q);
        const descMatch = card.desc?.toLowerCase().includes(q);
        return nameMatch || descMatch;
      });

      const sorted = filtered.sort((a, b) => {
        const sortA = getSortValue(a);
        const sortB = getSortValue(b);
        if (sortA !== sortB) return sortA - sortB;
        return (a.name || '').localeCompare(b.name || '');
      });

      setResults(sorted);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };



  const isExtraDeckType = (card) => {
    const types = ['Fusion', 'Synchro', 'Link', 'XYZ'];
    return types.some(type => card.type?.includes(type));
  };

  const totalCardCopies = (card) => {
    return (
      mainDeck.filter(c => c.id === card.id).length +
      extraDeck.filter(c => c.id === card.id).length +
      sideDeck.filter(c => c.id === card.id).length
    );
  };

  const addCard = (card, type) => {
    const isExtra = isExtraDeckType(card);
    const totalCopies = totalCardCopies(card);
    if (totalCopies >= 3) return;

    if (type === 'main' && !isExtra && mainDeck.length < 60) {
      setMainDeck(prev => [...prev, card]);
    } else if (type === 'extra' && isExtra && extraDeck.length < 15) {
      setExtraDeck(prev => [...prev, card]);
    } else if (type === 'side' && sideDeck.length < 15) {
      setSideDeck(prev => [...prev, card]);
    }
  };

  const removeCard = (index, type) => {
    if (type === 'main') setMainDeck(prev => prev.filter((_, i) => i !== index));
    else if (type === 'extra') setExtraDeck(prev => prev.filter((_, i) => i !== index));
    else if (type === 'side') setSideDeck(prev => prev.filter((_, i) => i !== index));
  };

  const saveAsNewDeck = () => {
    setSavingAsNew(true);
    try {
      if (!deckNameInput.trim()) return;
      
      // Check if a local deck with the same name already exists
      const existingDeckIndex = savedDecks.findIndex(deck => 
        deck.source === 'local' && deck.name === deckNameInput.trim()
      );
      
      const newDeck = {
        name: deckNameInput.trim(),
        main: [...mainDeck],
        extra: [...extraDeck],
        side: [...sideDeck],
        source: 'local', // ✅ THIS IS MANDATORY
      };
      
      let updatedSavedDecks;
      let newSelectedIndex;
      
      if (existingDeckIndex !== -1) {
        // Override existing deck
        updatedSavedDecks = [...savedDecks];
        updatedSavedDecks[existingDeckIndex] = newDeck;
        newSelectedIndex = existingDeckIndex;
        toast.success('✅ Existing deck updated!');
      } else {
        // Create new deck
        updatedSavedDecks = [...savedDecks, newDeck];
        newSelectedIndex = savedDecks.length;
        toast.success('Deck saved as new!');
      }
      
      // Update state
      setSavedDecks(updatedSavedDecks);
      setSelectedDeckIndex(newSelectedIndex);
      
      // Update localStorage
      const localDecks = updatedSavedDecks.filter(deck => deck.source === 'local');
      localStorage.setItem('savedDecks', JSON.stringify(localDecks));
      
    } finally {
      setSavingAsNew(false);
    }
  };

  const saveDeck = () => {
    setSaving(true);
    try {
      if (selectedDeckIndex === null) return;
      const updated = [...savedDecks];
      updated[selectedDeckIndex] = {
        name: updated[selectedDeckIndex].name,
        main: [...mainDeck],
        extra: [...extraDeck],
        side: [...sideDeck],
        source: 'local' // Preserve the source property
      };
      setSavedDecks(updated);
      toast.success('Deck saved!');
    } finally {
      setSaving(false);
    }
  };

  const handlePvpDuel = async () => {
    const selectedDeck = savedDecks[selectedDeckIndex];
    if (!selectedDeck) {
      toast.error('Please select a deck first!');
      return;
    }

    let resolvedDeck = selectedDeck;

    // ✅ If it's online, re-resolve IDs now
    if (selectedDeck.source === 'online') {
      try {
        const user = getAuth().currentUser;
        if (!user) {
          toast.error('You must be logged in.');
          return;
        }

        const db = getFirestore();
        const deckRef = doc(db, 'users', user.uid, 'decks', selectedDeck.id);
        const snapshot = await getDoc(deckRef);

        if (!snapshot.exists()) {
          toast.error('Deck not found.');
          return;
        }

        const deckData = snapshot.data();

        let cardDB = cardDatabase;
        if (!cardDB) {
          const res = await fetch('https://db.ygoprodeck.com/api/v7/cardinfo.php');
          const data = await res.json();
          cardDB = data.data || [];
          cardDB = Array.from(new Map(cardDB.map(c => [String(c.id), c])).values());
          setCardDatabase(cardDB);
        }

        const resolve = (idList) =>
          idList.map(id =>
            cardDB.find(c => String(c.id) === String(id)) ||
            cardDB.find(c => c.misc_info?.alias?.includes(Number(id))) ||
            cardDB.find(c => c.card_images?.some(img => String(img.id) === String(id))) ||
            {
              id,
              name: `Unknown (${id})`,
              card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
            }
          );

        resolvedDeck = {
          name: deckData.name || 'Online Deck',
          main: resolve(deckData.main || []),
          extra: resolve(deckData.extra || []),
          side: resolve(deckData.side || []),
        };
      } catch (err) {
        toast.error('Failed to load deck from server.');
        return;
      }
    }

    const shuffledMain = shuffleArray(resolvedDeck.main);
    const hand = shuffledMain.slice(0, 5);
    const restMain = shuffledMain.slice(5);

    localStorage.setItem('handTestDeck', JSON.stringify({
      ...resolvedDeck,
      main: restMain,
      hand,
    }));

    navigate('/room');
  };


  const renameDeck = () => {
    setRenaming(true);
    try {
      if (selectedDeckIndex === null || !deckNameInput.trim()) return;
      const updated = [...savedDecks];
      updated[selectedDeckIndex].name = deckNameInput.trim();
      setSavedDecks(updated);
      toast.success('Deck renamed!');
    } finally {
      setRenaming(false);
    }
  };

  const clearDeck = () => {
    setMainDeck([]);
    setExtraDeck([]);
    setSideDeck([]);
  };

  const deleteDeck = async () => {
    setDeleting(true);
    try {
      if (selectedDeckIndex === null) return;

      const deckToDelete = savedDecks[selectedDeckIndex];

      if (deckToDelete.source === 'online' && deckToDelete.id) {
        try {
          const user = getAuth().currentUser;
          if (user) {
            const db = getFirestore();
            const deckRef = doc(db, 'users', user.uid, 'decks', deckToDelete.id);
            await deleteDoc(deckRef);
            toast.success('✅ Deleted', deckToDelete.id);
          }
        } catch (err) {
          toast.error('Failed to delete online deck.');
        }
      }

      // Remove from local state
      const updated = savedDecks.filter((_, i) => i !== selectedDeckIndex);
      setSavedDecks(updated);
      
      // Update localStorage immediately for local decks
      if (deckToDelete.source === 'local') {
        const localDecks = updated.filter(deck => deck.source === 'local');
        localStorage.setItem('savedDecks', JSON.stringify(localDecks));
      }
      
      setSelectedDeckIndex(null);
      clearDeck();
      toast.success('Deck deleted!');
    } finally {
      setDeleting(false);
    }
  };


  const shuffleDeck = () => {
    setMainDeck(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  const sortDeck = () => {
    const monsters = [...mainDeck].filter(c => c.type?.includes('Monster'));
    const spells = [...mainDeck].filter(c => c.type?.includes('Spell'));
    const traps = [...mainDeck].filter(c => c.type?.includes('Trap'));
    monsters.sort((a, b) => (b.level || 0) - (a.level || 0));
    setMainDeck([...monsters, ...spells, ...traps]);
  };
  const resolveCardById = (id) => {
    if (!cardDatabase) return {
      id,
      name: `Unknown (${id})`,
      card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
    };

    return cardDatabase.find(c => String(c.id) === String(id)) ||
      cardDatabase.find(c => Array.isArray(c.misc_info?.alias) && c.misc_info.alias.includes(Number(id))) ||
      cardDatabase.find(c => c.card_images?.some(img => String(img.id) === String(id))) || {
      id,
      name: `Unknown (${id})`,
      card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
    };
  };

  const loadDeck = (index) => {
    // Validate the index and deck existence
    if (index === null || index === undefined || index < 0 || index >= savedDecks.length) {
      console.warn('Invalid deck index:', index, 'savedDecks length:', savedDecks.length);
      setSelectedDeckIndex(null);
      setDeckNameInput('');
      localStorage.removeItem('lastSelectedDeckIndex');
      return;
    }

    const deck = savedDecks[index];
    
    // Validate the deck object
    if (!deck || !deck.name) {
      console.warn('Invalid deck object at index:', index, deck);
      setSelectedDeckIndex(null);
      setDeckNameInput('');
      localStorage.removeItem('lastSelectedDeckIndex');
      return;
    }

    setSelectedDeckIndex(index);
    setDeckNameInput(deck.name);
    
    // Save the selected deck index to localStorage
    localStorage.setItem('lastSelectedDeckIndex', index.toString());

    // If deck source is local, the structure is already full objects
    if (deck.source === 'local') {
      setMainDeck(deck.main || []);
      setExtraDeck(deck.extra || []);
      setSideDeck(deck.side || []);
    }

    // If deck source is online, it only stores IDs; we need to fetch from DB
    else if (deck.source === 'online') {
      const fetchAndResolve = async () => {
        let db = cardDatabase;
        if (!db) {
          try {
            const res = await fetch('https://db.ygoprodeck.com/api/v7/cardinfo.php');
            const data = await res.json();
            db = data.data || [];
            db = Array.from(new Map(db.map(c => [String(c.id), c])).values());
            setCardDatabase(db);
          } catch {
            toast.error('Failed to fetch card database');
            return;
          }
        }

        const resolve = (idList) => idList.map(id =>
          db.find(c => String(c.id) === String(id)) ||
          db.find(c => Array.isArray(c.misc_info?.alias) && c.misc_info.alias.includes(Number(id))) ||
          db.find(c => c.card_images?.some(img => String(img.id) === String(id))) ||
          {
            id,
            name: `Unknown (${id})`,
            card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }]
          }
        );

        const resolvedMain = resolve(deck.main || []);
        const resolvedExtra = resolve(deck.extra || []);
        const resolvedSide = resolve(deck.side || []);

        setMainDeck(resolvedMain);
        setExtraDeck(resolvedExtra);
        setSideDeck(resolvedSide);
      };

      fetchAndResolve();
    }
  };


  const getSortValue = (card) => {
    if (!card.type) return 100;
    const name = card.name || '';
    const level = card.level || card.linkval || card.rank || 0;
    const lowerName = name.toLowerCase();

    if (card.type.includes('Monster') && !isExtraDeckType(card)) return 0 - level;
    if (card.type.includes('Spell')) {
      if (card.race === 'Normal') return 100;
      if (card.race === 'Quick-Play') return 101;
      if (card.race === 'Continuous') return 102;
      if (card.race === 'Field') return 103;
      return 104;
    }
    if (card.type.includes('Trap')) {
      if (card.race === 'Normal') return 110;
      if (card.race === 'Counter') return 111;
      if (card.race === 'Continuous') return 112;
      return 113;
    }
    if (card.type.includes('Fusion')) return 120 - level;
    if (card.type.includes('Synchro')) return 130 - level;
    if (card.type.includes('XYZ')) return 140 - (card.rank || 0);
    if (card.type.includes('Link')) return 150 - (card.linkval || 0);
    return 200;
  };

  const fetchUserTournaments = async (userId) => {
    setLoadingTournaments(true);
    try {
      const db = getFirestore();
      
      // Get all upcoming tournaments
      const upcomingRef = collection(db, 'tournament-v2', 'upcoming', 'list');
      const upcomingSnapshot = await getDocs(upcomingRef);
      
      const tournaments = [];
      
      for (const tournamentDoc of upcomingSnapshot.docs) {
        const tournamentData = tournamentDoc.data();
        const tournamentId = tournamentDoc.id;
        
        // Check if user is a participant in this tournament
        const participantRef = doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId, 'participants', userId);
        const participantDoc = await getDoc(participantRef);
        
        if (participantDoc.exists()) {
          // Debug: Log the tournament data to see the structure
          console.log('Tournament data:', tournamentData);
          console.log('Tournament ID:', tournamentId);
          
          tournaments.push({
            id: tournamentId,
            name: tournamentData.title || tournamentData.tournamentName || tournamentData.name || tournamentId || 'Unnamed Tournament',
            status: tournamentData.status || 'upcoming',
            started: tournamentData.started || false
          });
        }
      }
      
      setUserTournaments(tournaments);
    } catch (error) {
      console.error('Error fetching user tournaments:', error);
    } finally {
      setLoadingTournaments(false);
    }
  };

  const submitTournamentDeck = async (tournament) => {
    const user = getAuth().currentUser;
    if (!user) {
      toast.error('You must be logged in to submit a tournament deck.');
      return;
    }

    // Check if there are cards in the current deck
    if (mainDeck.length === 0 && extraDeck.length === 0 && sideDeck.length === 0) {
      toast.error('Please add some cards to your deck before submitting.');
      return;
    }

    try {
      const db = getFirestore();
      
      // Store tournament deck in the new location: /tournament-v2/upcoming/list/{tournamentId}/participants/{userId}/submittedDeck/current
      const tournamentDeckRef = doc(db, 'tournament-v2', 'upcoming', 'list', tournament.id, 'participants', user.uid, 'submittedDeck', 'current');
      
      // Create tournament deck data with tournament name
      const tournamentDeck = {
        name: tournament.name, // Use tournament name as deck name
        main: mainDeck.map(c => Number(c.id)),
        extra: extraDeck.map(c => Number(c.id)),
        side: sideDeck.map(c => Number(c.id)),
        tournamentId: tournament.id,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Use setDoc to create or override the deck
      await setDoc(tournamentDeckRef, tournamentDeck);
      
      // Check if a local deck with the same name already exists
      const existingDeckIndex = savedDecks.findIndex(deck => 
        deck.source === 'local' && deck.name === tournament.name
      );
      
      const newLocalDeck = {
        name: tournament.name, // Use tournament name as deck name
        main: [...mainDeck],
        extra: [...extraDeck],
        side: [...sideDeck],
        source: 'local',
        tournamentId: tournament.id // Add tournament ID to identify it
      };
      
      let updatedSavedDecks;
      let newSelectedIndex;
      
      if (existingDeckIndex !== -1) {
        // Override existing deck
        updatedSavedDecks = [...savedDecks];
        updatedSavedDecks[existingDeckIndex] = newLocalDeck;
        newSelectedIndex = existingDeckIndex;
        toast.success(`✅ Tournament deck "${tournament.name}" submitted and updated locally!`);
      } else {
        // Create new deck
        updatedSavedDecks = [...savedDecks, newLocalDeck];
        newSelectedIndex = savedDecks.length;
        toast.success(`✅ Tournament deck "${tournament.name}" submitted and saved locally!`);
      }
      
      // Update state
      setSavedDecks(updatedSavedDecks);
      setSelectedDeckIndex(newSelectedIndex);
      setDeckNameInput(tournament.name); // Set the input to tournament name
      
      // Update localStorage
      const localDecks = updatedSavedDecks.filter(deck => deck.source === 'local');
      localStorage.setItem('savedDecks', JSON.stringify(localDecks));
      
    } catch (error) {
      console.error('Error submitting tournament deck:', error);
      toast.error('Failed to submit tournament deck.');
      throw error;
    }
  };

  // Add a ref for the decklist area
  const decklistRef = React.useRef(null);

  // Add a screenshot handler
  const handleScreenshot = async () => {
    if (!decklistRef.current) return;
    const canvas = await html2canvas(decklistRef.current, { backgroundColor: '#18181c' });
    const link = document.createElement('a');
    link.download = 'decklist.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="deckbuilder-page" onClick={closeAllModals}>
      <div className="deck-controls">
        <div className='deck-controls-input'>
          {/* Label + Select */}
          <div className="deck-control-group">
            <label htmlFor="deck-select" className="deck-control-label">Select your deck</label>
            <select
              id="deck-select"
              className="deck-controls-select"
              value={selectedDeckIndex ?? ''}
              onChange={(e) => loadDeck(Number(e.target.value))}
            >
              <option value="" disabled>Select a deck</option>
              {savedDecks.map((deck, idx) => (
                <option key={idx} value={idx}>
                  {deck.name} {deck.source === 'online' ? '(Online)' : ''}
                </option>
              ))}
            </select>

          </div>

          {/* Label + Input */}
          <div className="deck-control-group">
            <label htmlFor="deck-name" className="deck-control-label">Update Deck</label>
            <input
              id="deck-name"
              className="deck-controls-name"
              type="text"
              placeholder="Deck name"
              value={deckNameInput}
              onChange={(e) => setDeckNameInput(e.target.value)}
            />
          </div>

        </div>


        {/* Buttons */}
        <div className="deck-buttons">
          {/* Action/Save Button */}
          <button 
            className="deckbuilder-action-save-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowActionsModal(true);
            }}
          >
            Actions
          </button>

          {/* Tools Button */}
          <button 
            className="deckbuilder-action-save-button shuffle-sort-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowToolsModal(true);
            }}
          >
            Tools
          </button>

          <button className="learn-combo" onClick={handleStartDuel}>Hand Test</button>
          {/* <button onClick={handlePvpDuel}>Pvp Duel</button> */}
          {/* <button disabled>PvP</button> */}
          <button 
            className="deckbuilder-action-save-button tournament-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowTournamentModal(true);
            }}
          >
            Tournament
          </button>

          {/* YDKE Button */}
          <button 
            className="deckbuilder-action-save-button ydke-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowImportExportModal(true);
            }}
          >
            Import/Export
          </button>
        </div>
      </div>

      {/* Column 3: Search Panel */}
      <div className="card-search">
        <button
          onClick={() => {
            setShowSearchModal(true);
            window.setCardSearchOpen?.(true); // ✅ hide global settings/modal
          }}
          style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
        >
          Search Cards
        </button>

      </div>

      <div className="deckbuilder-columns">
        {/* Column 2: Deck Panel */}
        <div ref={decklistRef} className="deck-panels">
  {/* Main Deck Section */}
  <div className="deck-section">
    <div className="deck-section-header">
      <span>Main Deck ({mainDeck.length}/60)</span>
      <span>
        Monster {mainDeck.filter(c => c.type?.includes('Monster')).length} &nbsp;
        Spell {mainDeck.filter(c => c.type?.includes('Spell')).length} &nbsp;
        Trap {mainDeck.filter(c => c.type?.includes('Trap')).length}
      </span>
    </div>
    <div className="deck-row">
      {mainDeck.map((card, idx) => {
        const count = mainDeck.filter(c => c.id === card.id).length;
        // Only show the first occurrence of each card
        if (mainDeck.findIndex(c => c.id === card.id) !== idx) return null;
        return (
          <div
            key={idx}
            className="deck-card-slot"
            onClick={() => {
              setSelectedCard(card);
              setShowCardInfoModal(true);
            }}
            onContextMenu={e => {
              e.preventDefault();
              // Remove one copy of this card from mainDeck
              const removeIdx = mainDeck.findIndex(c => c.id === card.id);
              if (removeIdx !== -1) removeCard(removeIdx, 'main');
            }}
            onTouchStart={e => {
              // Long-press detection for mobile with scroll prevention
              const touchStartY = e.touches[0].clientY;
              const touchStartX = e.touches[0].clientX;
              let hasMoved = false;
              let timer = null;
              
              const handleTouchMove = (moveEvent) => {
                const touchY = moveEvent.touches[0].clientY;
                const touchX = moveEvent.touches[0].clientX;
                const deltaY = Math.abs(touchY - touchStartY);
                const deltaX = Math.abs(touchX - touchStartX);
                
                // If moved more than 10px in any direction, cancel the long-press
                if (deltaY > 10 || deltaX > 10) {
                  hasMoved = true;
                  if (timer) {
                    clearTimeout(timer);
                    timer = null;
                  }
                }
              };
              
              const handleTouchEnd = () => {
                if (timer) {
                  clearTimeout(timer);
                  timer = null;
                }
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };
              
              timer = setTimeout(() => {
                if (!hasMoved) {
                  const removeIdx = mainDeck.findIndex(c => c.id === card.id);
                  if (removeIdx !== -1) removeCard(removeIdx, 'main');
                }
                handleTouchEnd();
              }, 800); // Increased delay to 800ms for better UX
              
              document.addEventListener('touchmove', handleTouchMove, { passive: true });
              document.addEventListener('touchend', handleTouchEnd, { once: true });
            }}
          >
            <img
              src={card.card_images[0].image_url}
              alt={card.name}
              className="deck-card-img"
              draggable={false}
            />
            {count > 1 && <span className="card-count-badge">{count}</span>}
          </div>
        );
      })}
    </div>
  </div>

  {/* Extra Deck Section */}
  <div className="deck-section">
    <div className="deck-section-header">
      <span>Extra Deck ({extraDeck.length}/15)</span>
      <span>
        Fusion {extraDeck.filter(c => c.type?.includes('Fusion')).length} &nbsp;
        Xyz {extraDeck.filter(c => c.type?.includes('XYZ')).length} &nbsp;
        Synchro {extraDeck.filter(c => c.type?.includes('Synchro')).length} &nbsp;
        Link {extraDeck.filter(c => c.type?.includes('Link')).length}
      </span>
    </div>
    <div className="deck-row">
      {extraDeck.map((card, idx) => {
        const count = extraDeck.filter(c => c.id === card.id).length;
        if (extraDeck.findIndex(c => c.id === card.id) !== idx) return null;
        return (
          <div
            key={idx}
            className="deck-card-slot"
            onClick={() => {
              setSelectedCard(card);
              setShowCardInfoModal(true);
            }}
            onContextMenu={e => {
              e.preventDefault();
              // Remove one copy of this card from extraDeck
              const removeIdx = extraDeck.findIndex(c => c.id === card.id);
              if (removeIdx !== -1) removeCard(removeIdx, 'extra');
            }}
            onTouchStart={e => {
              // Long-press detection for mobile with scroll prevention
              const touchStartY = e.touches[0].clientY;
              const touchStartX = e.touches[0].clientX;
              let hasMoved = false;
              let timer = null;
              
              const handleTouchMove = (moveEvent) => {
                const touchY = moveEvent.touches[0].clientY;
                const touchX = moveEvent.touches[0].clientX;
                const deltaY = Math.abs(touchY - touchStartY);
                const deltaX = Math.abs(touchX - touchStartX);
                
                // If moved more than 10px in any direction, cancel the long-press
                if (deltaY > 10 || deltaX > 10) {
                  hasMoved = true;
                  if (timer) {
                    clearTimeout(timer);
                    timer = null;
                  }
                }
              };
              
              const handleTouchEnd = () => {
                if (timer) {
                  clearTimeout(timer);
                  timer = null;
                }
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };
              
              timer = setTimeout(() => {
                if (!hasMoved) {
                  const removeIdx = extraDeck.findIndex(c => c.id === card.id);
                  if (removeIdx !== -1) removeCard(removeIdx, 'extra');
                }
                handleTouchEnd();
              }, 800); // Increased delay to 800ms for better UX
              
              document.addEventListener('touchmove', handleTouchMove, { passive: true });
              document.addEventListener('touchend', handleTouchEnd, { once: true });
            }}
          >
            <img
              src={card.card_images[0].image_url}
              alt={card.name}
              className="deck-card-img"
              draggable={false}
            />
            {count > 1 && <span className="card-count-badge">{count}</span>}
          </div>
        );
      })}
    </div>
  </div>

  {/* Side Deck Section */}
  <div className="deck-section">
    <div className="deck-section-header">
      <span>Side Deck ({sideDeck.length}/15)</span>
      <span>
        Monster {sideDeck.filter(c => c.type?.includes('Monster')).length} &nbsp;
        Spell {sideDeck.filter(c => c.type?.includes('Spell')).length} &nbsp;
        Trap {sideDeck.filter(c => c.type?.includes('Trap')).length}
      </span>
    </div>
    <div className="deck-row">
      {sideDeck.map((card, idx) => {
        const count = sideDeck.filter(c => c.id === card.id).length;
        if (sideDeck.findIndex(c => c.id === card.id) !== idx) return null;
        return (
          <div
            key={idx}
            className="deck-card-slot"
            onClick={() => {
              setSelectedCard(card);
              setShowCardInfoModal(true);
            }}
            onContextMenu={e => {
              e.preventDefault();
              // Remove one copy of this card from sideDeck
              const removeIdx = sideDeck.findIndex(c => c.id === card.id);
              if (removeIdx !== -1) removeCard(removeIdx, 'side');
            }}
            onTouchStart={e => {
              // Long-press detection for mobile with scroll prevention
              const touchStartY = e.touches[0].clientY;
              const touchStartX = e.touches[0].clientX;
              let hasMoved = false;
              let timer = null;
              
              const handleTouchMove = (moveEvent) => {
                const touchY = moveEvent.touches[0].clientY;
                const touchX = moveEvent.touches[0].clientX;
                const deltaY = Math.abs(touchY - touchStartY);
                const deltaX = Math.abs(touchX - touchStartX);
                
                // If moved more than 10px in any direction, cancel the long-press
                if (deltaY > 10 || deltaX > 10) {
                  hasMoved = true;
                  if (timer) {
                    clearTimeout(timer);
                    timer = null;
                  }
                }
              };
              
              const handleTouchEnd = () => {
                if (timer) {
                  clearTimeout(timer);
                  timer = null;
                }
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };
              
              timer = setTimeout(() => {
                if (!hasMoved) {
                  const removeIdx = sideDeck.findIndex(c => c.id === card.id);
                  if (removeIdx !== -1) removeCard(removeIdx, 'side');
                }
                handleTouchEnd();
              }, 800); // Increased delay to 800ms for better UX
              
              document.addEventListener('touchmove', handleTouchMove, { passive: true });
              document.addEventListener('touchend', handleTouchEnd, { once: true });
            }}
          >
            <img
              src={card.card_images[0].image_url}
              alt={card.name}
              className="deck-card-img"
              draggable={false}
            />
            {count > 1 && <span className="card-count-badge">{count}</span>}
          </div>
        );
      })}
    </div>
  </div>
</div>


      </div>
      {showYDKEImport && (
        <div className="ydke-modal-backdrop">
          <div className="ydke-modal">
            <h2>Import YDKE Deck</h2>
            <textarea
              value={ydkeInput}
              onChange={(e) => setYDKEInput(e.target.value)}
              placeholder="Paste your YDKE code here"
            />
            <div className="ydke-modal-buttons">
              <button
                onClick={async () => {
                  const code = ydkeInput.trim();
                  if (!code.startsWith('ydke://')) {
                    toast.error('Invalid YDKE code');
                    return;
                  }

                  let db = cardDatabase;

                  if (!db || db.length === 0) {
                    try {
                      const res = await fetch(`https://db.ygoprodeck.com/api/v7/cardinfo.php`);
                      const data = await res.json();
                      db = data.data || [];
                      // ✅ Remove duplicates by ID
                      db = Array.from(new Map(db.map(c => [String(c.id), c])).values());
                      setCardDatabase(db); // Cache it
                    } catch (err) {
                      toast.error('Failed to load card database');
                      return;
                    }
                  } else {
                    // ✅ Even cached DB might benefit from cleanup
                    db = Array.from(new Map(db.map(c => [String(c.id), c])).values());
                  }


                  const decodeYDKE = (ydke) => {
                    const cleaned = ydke.replace(/^ydke:\/\//, '');
                    const [main64, extra64, side64] = cleaned.split('!');
                    const decode = (b64) => {
                      if (!b64) return [];
                      const binary = atob(b64);
                      const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
                      const ids = [];
                      for (let i = 0; i < bytes.length; i += 4) {
                        const id = (bytes[i]) |
                          (bytes[i + 1] << 8) |
                          (bytes[i + 2] << 16) |
                          (bytes[i + 3] << 24);
                        ids.push(id >>> 0);
                      }
                      return ids;
                    };
                    return {
                      main: decode(main64),
                      extra: decode(extra64),
                      side: decode(side64),
                    };
                  };

                  const ids = decodeYDKE(code);
                  const resolveCards = (idList) => {
                    return idList.map(id => {
                      // 1. Direct match
                      let found = db.find(c => String(c.id) === String(id));

                      // 2. Alias match
                      if (!found) {
                        found = db.find(c =>
                          Array.isArray(c.misc_info?.alias) &&
                          c.misc_info.alias.includes(Number(id))
                        );
                        if (found) {
                          console.warn(`[YDKE] ID ${id} not found directly, resolved by alias: ${found.name}`);
                        }
                      }

                      // 3. Variant artwork match by card_images[].id
                      if (!found) {
                        found = db.find(c =>
                          Array.isArray(c.card_images) &&
                          c.card_images.some(img => String(img.id) === String(id))
                        );
                        if (found) {
                          console.warn(`[YDKE] ID ${id} matched variant image of: ${found.name}`);
                        }
                      }

                      // 4. Fallback
                      if (!found) {
                        console.warn(`[YDKE] ${id} not found in db`);
                        return {
                          id,
                          name: `Unknown (${id})`,
                          card_images: [
                            { image_url: 'https://via.placeholder.com/80x115?text=Unknown' }
                          ]
                        };
                      }

                      return found;
                    });
                  };


                  setMainDeck(resolveCards(ids.main));
                  setExtraDeck(resolveCards(ids.extra));
                  setSideDeck(resolveCards(ids.side));
                  setShowYDKEImport(false);
                  setYDKEInput('');
                }}
              >
                Import
              </button>
              <button onClick={() => setShowYDKEImport(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showSearchModal && (
        <div className="card-search-modal-overlay" onClick={() => {
          setShowSearchModal(false);
          window.setCardSearchOpen?.(false);
        }}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => {
              setShowSearchModal(false);
              window.setCardSearchOpen?.(false);
            }}>✕</button>

            <input
              type="text"
              placeholder="Search for a card"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchCards()}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                backgroundColor: '#222',
                color: '#fff',
                border: '1px solid #555',
                borderRadius: '8px',
                marginBottom: '16px',
              }}
              autoFocus
            />
            <button
              onClick={searchCards}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '1rem',
                backgroundColor: '#e63939',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                marginBottom: '16px',
                cursor: 'pointer'
              }}
            >
              🔍 Search
            </button>


            <div className="search-results">
              {loading && <div className="loading-spinner">Loading...</div>}

              {results.map((card, index) => {
                const totalCopies = totalCardCopies(card);
                const isExtra = isExtraDeckType(card);

                return (
                  <div
                    key={index}
                    className="search-card"
                    onClick={() => { setSelectedCard(card) }}
                  >
                    {/* 🧩 Image + Info row */}
                    <div className="card-info-img-general">
                      <img
                        onContextMenu={(e) => e.preventDefault()}
                        onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                        onTouchStart={(e) => e.preventDefault()}
                        draggable={false}
                        src={card.card_images[0].image_url}
                        alt={card.name}
                        className="search-card-image"
                      />



                      <div className="card-info-general">
                        <div><strong>Name:</strong> {card.name}</div>
                        {card.attribute && <div><strong>Attribute:</strong> {card.attribute}</div>}
                        {card.race && <div><strong>Type:</strong> {card.race}</div>}
                        {(card.level || card.rank || card.linkval) && (
                          <div>
                            <strong>{card.linkval ? 'Link Rating' : card.rank ? 'Rank' : 'Level'}:</strong>{' '}
                            {card.linkval || card.rank || card.level}
                          </div>
                        )}
                        {card.scale !== undefined && (
                          <div><strong>Pendulum Scale:</strong> {card.scale}</div>
                        )}
                        {(card.atk !== undefined || card.def !== undefined) && (
                          <div>
                            <strong>ATK/DEF:</strong> {card.atk !== undefined ? card.atk : '-'} /
                            {card.def !== undefined ? ` ${card.def}` : ' -'}
                          </div>
                        )}
                        {/* Add buttons */}
                        <div className="add-buttons">
                          <button onClick={() => addCard(card, 'main')} disabled={isExtra || totalCopies >= 3}>+ Main</button>
                          <button onClick={() => addCard(card, 'extra')} disabled={!isExtra || totalCopies >= 3}>+ Extra</button>
                          <button onClick={() => addCard(card, 'side')} disabled={totalCopies >= 3}>+ Side</button>
                        </div>
                      </div>
                    </div>

                    {/* 📜 Effect text */}
                    <p className="search-card-desc">
                      {
                        card.desc
                          ?.replace(/(?=●|\[.*?\])/g, '\n')
                          .replace(/\\n/g, '\n')
                      }
                    </p>


                  </div>
                );
              })}
            </div>


          </div>
        </div>
      )}
      {showCardInfoModal && selectedCard && (
        <div
          className="card-search-modal-overlay"
          onClick={() => setShowCardInfoModal(false)}
        >
          <div
            className="card-search-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-card-search"
              onClick={() => setShowCardInfoModal(false)}
            >
              ✕
            </button>

            <div className="card-info-img-general" style={{ marginBottom: '16px' }}>
              <img
                onContextMenu={(e) => e.preventDefault()}
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={selectedCard.card_images[0].image_url}
                alt={selectedCard.name}
                className="search-card-image"
              />

              <div className="card-info-general">
                <div><strong>Name:</strong> {selectedCard.name}</div>
                {selectedCard.attribute && <div><strong>Attribute:</strong> {selectedCard.attribute}</div>}
                {selectedCard.race && <div><strong>Type:</strong> {selectedCard.race}</div>}
                {(selectedCard.level || selectedCard.rank || selectedCard.linkval) && (
                  <div>
                    <strong>{selectedCard.linkval ? 'Link Rating' : selectedCard.rank ? 'Rank' : 'Level'}:</strong>{' '}
                    {selectedCard.linkval || selectedCard.rank || selectedCard.level}
                  </div>
                )}
                {selectedCard.scale !== undefined && (
                  <div><strong>Pendulum Scale:</strong> {selectedCard.scale}</div>
                )}
                {(selectedCard.atk !== undefined || selectedCard.def !== undefined) && (
                  <div>
                    <strong>ATK/DEF:</strong> {selectedCard.atk !== undefined ? selectedCard.atk : '-'} /
                    {selectedCard.def !== undefined ? ` ${selectedCard.def}` : ' -'}
                  </div>
                )}
              </div>
            </div>

            {/* 📜 Effect text */}
            <p className="search-card-desc">
              {
                selectedCard.desc
                  ?.replace(/(?=●|\[.*?\])/g, '\n')
                  .replace(/\\n/g, '\n')
              }
            </p>
          </div>
        </div>
      )}

      {!showSearchModal && (
        <button className="back-button" onClick={() => {
          playSound('return');
          navigate('/tool');
        }}>
          {t('cardSearch.back')}
        </button>
      )}

      {/* Actions Modal */}
      {showActionsModal && (
        <div className="card-search-modal-overlay" onClick={() => setShowActionsModal(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowActionsModal(false)}>✕</button>
            <h2>Deck Actions</h2>
            <div className="modal-buttons-grid">
              <button onClick={() => {
                saveAsNewDeck();
                setShowActionsModal(false);
              }} disabled={savingAsNew}>
                {savingAsNew ? 'Saving...' : 'Save As'}
              </button>
              <button onClick={() => {
                saveDeck();
                setShowActionsModal(false);
              }} disabled={selectedDeckIndex === null || saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => {
                saveDeckOnline();
                setShowActionsModal(false);
              }} disabled={savingOnline}>
                {savingOnline ? 'Saving...' : `Save Online ${onlineDeckCount}/5`}
              </button>
              <button onClick={() => {
                renameDeck();
                setShowActionsModal(false);
              }} disabled={selectedDeckIndex === null || renaming}>
                {renaming ? 'Renaming...' : 'Rename'}
              </button>
              <button onClick={() => {
                clearDeck();
                setShowActionsModal(false);
              }}>Clear</button>
              <button onClick={() => {
                deleteDeck();
                setShowActionsModal(false);
              }} disabled={selectedDeckIndex === null || deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tools Modal */}
      {showToolsModal && (
        <div className="card-search-modal-overlay" onClick={() => setShowToolsModal(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowToolsModal(false)}>✕</button>
            <h2>Deck Tools</h2>
            <div className="modal-buttons-grid">
              <button onClick={() => {
                shuffleDeck();
                setShowToolsModal(false);
              }}>Shuffle</button>
              <button onClick={() => {
                sortDeck();
                setShowToolsModal(false);
              }}>Sort</button>
            </div>
          </div>
        </div>
      )}

      {/* Import/Export Modal */}
      {showImportExportModal && (
        <div className="card-search-modal-overlay" onClick={() => setShowImportExportModal(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowImportExportModal(false)}>✕</button>
            <h2>Import/Export</h2>
            <div className="modal-buttons-grid">
              <button onClick={() => {
                const encodeYDKE = (main, extra, side) => {
                  const toBytes = (cards) => {
                    const bytes = [];
                    for (const card of cards) {
                      const id = Number(card.id);
                      if (!id || isNaN(id)) continue;
                      bytes.push(id & 0xff);
                      bytes.push((id >> 8) & 0xff);
                      bytes.push((id >> 16) & 0xff);
                      bytes.push((id >> 24) & 0xff);
                    }
                    return bytes;
                  };

                  const toBase64 = (bytes) => {
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) {
                      binary += String.fromCharCode(bytes[i]);
                    }
                    return btoa(binary);
                  };

                  const main64 = toBase64(toBytes(main));
                  const extra64 = toBase64(toBytes(extra));
                  const side64 = toBase64(toBytes(side));
                  const result = `ydke://${main64}!${extra64}!${side64}!`;
                  console.log('[✅ FINAL Exported YDKE]', result);
                  return result;
                };

                const isValidCard = (c) => c?.id && !isNaN(Number(c.id)) && Number(c.id) > 1000;
                const cleanedMain = mainDeck.filter(isValidCard);
                const cleanedExtra = extraDeck.filter(isValidCard);
                const cleanedSide = sideDeck.filter(isValidCard);

                const code = encodeYDKE(cleanedMain, cleanedExtra, cleanedSide);
                navigator.clipboard.writeText(code);
                toast.success('✅ Clean YDKE copied to clipboard!');
                setShowImportExportModal(false);
              }}>
                Export YDKE
              </button>
              <button onClick={() => {
                setShowYDKEImport(true);
                setShowImportExportModal(false);
              }}>Import YDKE</button>
            </div>
          </div>
        </div>
      )}

      {/* Tournament Modal */}
      {showTournamentModal && (
        <div className="card-search-modal-overlay" onClick={() => setShowTournamentModal(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowTournamentModal(false)}>✕</button>
                         <h2>Tournament Deck Submission</h2>
             <p className="tournament-instructions">
               Build your tournament deck in the deck builder above, then click on a tournament to submit it.
             </p>
             
             {loadingTournaments ? (
               <div className="loading-spinner">Loading tournaments...</div>
             ) : userTournaments.length === 0 ? (
               <div className="no-tournaments">
                 <p>You are not participating in any upcoming tournaments.</p>
               </div>
             ) : (
               <div className="tournament-deck-submission-buttons">
                 {userTournaments.map((tournament) => (
                   <button
                     key={tournament.id}
                     className={`tournament-deck-submission-button ${tournament.started ? 'tournament-started' : ''}`}
                     onClick={async () => {
                       if (tournament.started) {
                         toast.error('Tournament has already started. Deck cannot be modified.');
                         return;
                       }
                       try {
                         await submitTournamentDeck(tournament);
                         setShowTournamentModal(false);
                       } catch (error) {
                         console.error('Failed to submit tournament deck:', error);
                         toast.error('Failed to submit deck for tournament');
                       }
                     }}
                     disabled={tournament.started}
                   >
                     Submit for {tournament.name}
                     {tournament.started && <span className="tournament-status"> (Started)</span>}
                   </button>
                 ))}
               </div>
             )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DeckBuilderPage;
